import { Injectable, OnDestroy } from '@angular/core';
import { AssetManager } from '@app/asset-manager/asset-manager';
import { GaugingElement } from '@app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { ElementType } from '@app/platform/busines-data/Framework/Region';
import { AssetInfoViewService } from '@app/template/asset-info-view/asset-info-view/asset-info-view.service';
import { ViewModeAnomalyLabelsService } from '@app/template/view-mode-tools/view-mode-anomaly-labels/view-mode-anomaly-labels.service';
import { Subscription } from 'rxjs';
import { StructureTypesEventsService } from '@app/template/view-mode-tools/view-mode-anomaly-labels/structure-types-events.service';
import { AssetPropertiesLoaderService } from '@app/asset-manager/asset-properties-loader.service';

export interface ViewModeOption {
  id: string;
  name: string;
  type: 'toggle' | 'slider';
  value?: boolean | number;
  min?: number;
  max?: number;
  step?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ViewModeInspectionService implements OnDestroy {

  allStructuralTypesSorted: string[] = ['Plate', 'Stiffener', 'FacePlate', 'Bracket', 'BeamPart', 'Cable', 'Pipe', "PipeConnector", "Anode"];
  availableStructureElementTypes: ElementType[] = [];
  selectedElementsTypes: ElementType[] = [];
  
  private regionChangeSubscription: Subscription;
  private currentRegionIds: string[] = [];
  private isInspectionViewModeActive: boolean = false;
  private inspectionPollingInterval: any;
  private lastInspectionCount: number = 0;

  // Visual options
  visualOptions: ViewModeOption[] = [
    {
      id: 'xray',
      name: 'X-Ray',
      type: 'toggle',
      value: false
    },
    {
      id: 'hideLabels',
      name: 'Hide Labels',
      type: 'toggle',
      value: false
    },
    // {
    //   id: 'wireframe',
    //   name: 'Wireframe',
    //   type: 'toggle',
    //   value: false
    // },
    // {
    //   id: 'transparency',
    //   name: 'Transparency',
    //   type: 'toggle',
    //   value: false
    // },
    // {
    //   id: 'opacity',
    //   name: 'Opacity',
    //   type: 'slider',
    //   value: 1.0,
    //   min: 0.1,
    //   max: 1.0,
    //   step: 0.1
    // }
  ];

  constructor(
    private assetManager: AssetManager,
    private assetInfoViewService: AssetInfoViewService,
    private viewModeAnomalyLabelsService: ViewModeAnomalyLabelsService,
    private structureTypesEventsService: StructureTypesEventsService,
    private assetPropertiesLoaderService: AssetPropertiesLoaderService
  ) {
    // Subscribe to region changes to update visible elements automatically
    this.regionChangeSubscription = this.assetInfoViewService.castCurrentRegionIds.subscribe((regionIds: string[]) => {
      if (regionIds && regionIds.length > 0) {
        //filter duplicate regionids
        const uniqueRegionIds = [...new Set(regionIds)];
        this.assetPropertiesLoaderService.waitUntilReady(uniqueRegionIds[0]).then(() => {
          this.handleRegionChange(uniqueRegionIds);
        });
      }
    });
  }

  handleRegionChange(regionIds: string[]): void {
    const regionCountChanged = regionIds.length !== this.currentRegionIds.length;
    const regionContentChanged = !regionIds.every(id => this.currentRegionIds.includes(id));

    const hasRegionChanged = regionCountChanged || regionContentChanged;

    if (hasRegionChanged) {
      this.currentRegionIds = [...regionIds];
      this.refreshViewMode();
    } else {
      this.updateVisibleElements();
    }
  }

  filterInspectionElementsByElementTypes(elementTypes: string[]): void {
    this.assetManager.asset.InspectionCampaigns.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement
        const targetTypeId = gaugingElement.targetElement?.TypeId?.toString();
        if (!targetTypeId) {
          return;
        }

        if(elementTypes.includes(targetTypeId)) {
          gaugingElement.SurfaceDesign.visible = true;
        } else {
          gaugingElement.SurfaceDesign.visible = false;
        }
      })
    })
  }

  selectAllStructureElements(availableElementTypes?: ElementType[]): ElementType[] {
    const elementsToSelect = availableElementTypes || this.availableStructureElementTypes;
    if (elementsToSelect.length > 0) {
      this.selectedElementsTypes = [...elementsToSelect];
      this.filterInspectionElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));

      this.structureTypesEventsService.emitStructureTypesChange(this.selectedElementsTypes);
      return this.selectedElementsTypes;
    }
    this.selectedElementsTypes = [];
    this.structureTypesEventsService.emitStructureTypesChange(this.selectedElementsTypes);
    return this.selectedElementsTypes;
  }

   unselectAllStructureElements() {
    this.selectedElementsTypes = []
    this.filterInspectionElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));
    this.structureTypesEventsService.emitStructureTypesChange(this.selectedElementsTypes);
  }

  updateVisibleElements(): void {
    // Store the currently selected element type IDs to preserve user selection
    const previouslySelectedIds = this.selectedElementsTypes.map(type => type.id);
    
    this.availableStructureElementTypes = [];
    
    for (const region of this.assetManager.asset.GetStructureVisibleRegions()) {
      for (const type of region.getRegionElementsTypes()) {
        if (!this.availableStructureElementTypes.some(existingType => existingType.id === type.id)) {
          this.availableStructureElementTypes.push(type);
        }
      }
    }
    
    this.availableStructureElementTypes.sort((a, b) => this.allStructuralTypesSorted.indexOf(a.name) - this.allStructuralTypesSorted.indexOf(b.name));
    
    // Restore the previous selection, but only for element types that are still available
    this.selectedElementsTypes = this.availableStructureElementTypes.filter(type => 
      previouslySelectedIds.includes(type.id)
    );
    
    if (this.isInspectionViewModeActive) {
      this.filterInspectionElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));
      this.structureTypesEventsService.emitStructureTypesChange(this.selectedElementsTypes);
    }

    this.syncAnomalyLabelsVisibility();
  }

  selectElementType(elementType: ElementType): void {
    if (this.selectedElementsTypes.includes(elementType)) {
      this.selectedElementsTypes = this.selectedElementsTypes.filter(type => type !== elementType);
    } else {
      this.selectedElementsTypes.push(elementType);
    }
    this.filterInspectionElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));
    this.structureTypesEventsService.emitStructureTypesChange(this.selectedElementsTypes);
    this.syncAnomalyLabelsVisibility();
  }

  toggleVisualOption(option: ViewModeOption): void {
    if (option.type === 'toggle') {
      option.value = !option.value;
      this.applyVisualOption(option);
    }
  }

  private applyVisualOption(option: ViewModeOption): void {
    switch (option.id) {
      case 'xray':
        this.assetManager.toggleXRayInspectionViewMode(option.value as boolean);
        break;
      
      case 'hideLabels':
        this.syncAnomalyLabelsVisibility();
        break;
      
      // case 'wireframe':
      //   this.toggleWireframe(option.value as boolean);
      //   break;
      
      // case 'transparency':
      //   if (option.value) {
      //     this.assetManager.changeToTransparenceViewMode();
      //   } else {
      //     this.assetManager.changeToDefaultViewMode();
      //   }
      //   break;
      
      // case 'opacity':
      //   this.setOpacity(option.value as number);
      //   break;
    }
  }

  refreshViewMode(): void {
    this.visualOptions.forEach(option => {
      if (option.type === 'toggle') {
        option.value = false;
      } else if (option.type === 'slider') {
        option.value = option.max || 1.0;
      }
      this.applyVisualOption(option);
    });

    this.updateVisibleElements();
    this.selectAllStructureElements();
    this.syncAnomalyLabelsVisibility();
  }

  /**
   * Activates inspection view mode with anomaly labels
   */
  activateInspectionViewMode(refresh: boolean = true): void {
    this.isInspectionViewModeActive = true;
    if (refresh) {
      this.refreshViewMode();
    } else {
      this.syncAnomalyLabelsVisibility();
    }
    this.startInspectionPolling();
  }

  /**
   * Deactivates inspection view mode and anomaly labels
   */
  deactivateInspectionViewMode(): void {
    this.isInspectionViewModeActive = false;
    this.stopInspectionPolling();
    this.setVisualOptionValue('xray', false);
    this.assetManager.toggleXRayInspectionViewMode(false);
    this.showAllInspectionElements();
    this.syncAnomalyLabelsVisibility();
  }

  /**
   * Restores inspection view mode state from a detailed visualization mode string
   * Format: "Inspection|activeOptions:elementIds"
   * Example: "Inspection|xray,hideLabels:1,2,3"
   */
  restoreInspectionViewMode(visualizationMode: string): void {
    // Parse the detailed mode string
    const parts = visualizationMode.split('|');
    if (parts.length !== 2) {
      // Fallback to basic inspection mode
      this.activateInspectionViewMode();
      return;
    }

    const [baseMode, details] = parts;
    if (baseMode !== 'Inspection') {
      // Fallback to basic inspection mode
      this.activateInspectionViewMode();
      return;
    }

    const [activeOptionsStr, elementIdsStr] = details.split(':');
    const activeOptionIds = activeOptionsStr && activeOptionsStr.trim()
      ? activeOptionsStr.split(',').map(id => id.trim())
      : [];

    this.isInspectionViewModeActive = true;
    this.startInspectionPolling();
    this.updateVisibleElements();

    // Restore visual options
    for (const option of this.visualOptions) {
      if (option.type === 'toggle') {
        option.value = activeOptionIds.includes(option.id);
      }
    }

    // Restore selected element types
    if (elementIdsStr && elementIdsStr.trim()) {
      const selectedElementIds = elementIdsStr.split(',').map(id => id.trim());
      this.selectedElementsTypes = this.availableStructureElementTypes.filter(type =>
        selectedElementIds.includes(type.id.toString())
      );

      // Apply the filtering
      this.filterInspectionElementsByElementTypes(selectedElementIds);
      this.structureTypesEventsService.emitStructureTypesChange(this.selectedElementsTypes);
    }

    if (!elementIdsStr || elementIdsStr.trim() === '') {
      this.unselectAllStructureElements();
    }

    this.visualOptions.forEach(option => this.applyVisualOption(option));
    this.syncAnomalyLabelsVisibility();
  }

  public reapplyInspectionViewMode(): void {
    if (!this.isInspectionViewModeActive) {
      return;
    }

    this.visualOptions.forEach(option => this.applyVisualOption(option));
    this.updateVisibleElements();
    this.syncAnomalyLabelsVisibility();
  }

  public get isActive(): boolean {
    return this.isInspectionViewModeActive;
  }

  public get selectedElementTypeIds(): string[] {
    return this.selectedElementsTypes.map(type => type.id?.toString());
  }

  public get xrayEnabled(): boolean {
    return this.getVisualOptionValue('xray');
  }

  public get anomalyLabelsHiddenByUser(): boolean {
    return this.getVisualOptionValue('hideLabels');
  }

  private syncAnomalyLabelsVisibility(): void {
    this.viewModeAnomalyLabelsService.setLabelsHiddenByUser(this.anomalyLabelsHiddenByUser);
    this.viewModeAnomalyLabelsService.syncWithVisibleAnomalies();
  }

  private getVisualOptionValue(optionId: string): boolean {
    return this.visualOptions.find(option => option.id === optionId)?.value === true;
  }

  private setVisualOptionValue(optionId: string, value: boolean): void {
    const option = this.visualOptions.find(item => item.id === optionId);
    if (option) {
      option.value = value;
    }
  }

  private showAllInspectionElements(): void {
    this.assetManager.asset?.InspectionCampaigns?.forEach((inspectionCollection) => {
      inspectionCollection.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement;
        if (gaugingElement.SurfaceDesign) {
          gaugingElement.SurfaceDesign.visible = true;
        }
      });
    });
  }

  ngOnDestroy(): void {
    if (this.regionChangeSubscription) {
      this.regionChangeSubscription.unsubscribe();
    }

    this.stopInspectionPolling();

    // Ensure anomaly labels are deactivated when service is destroyed
    this.viewModeAnomalyLabelsService.deactivateAnomalyLabels();
  }

  /**
   * Starts polling for inspection changes when the view mode is active
   */
  private startInspectionPolling(): void {
    this.stopInspectionPolling(); // Clear any existing interval
    this.lastInspectionCount = this.getCurrentInspectionCount();

    // Poll every 2 seconds to check for new inspections
    this.inspectionPollingInterval = setInterval(() => {
      if (!this.isInspectionViewModeActive) {
        this.stopInspectionPolling();
        return;
      }

      const currentCount = this.getCurrentInspectionCount();
      if (currentCount !== this.lastInspectionCount) {
        console.log(`[ViewModeInspectionService] Detected inspection change: ${this.lastInspectionCount} -> ${currentCount}`);
        this.lastInspectionCount = currentCount;

        // Use setTimeout to allow the DOM to update after the inspection change
        setTimeout(() => {
          this.updateVisibleElements();
          this.syncAnomalyLabelsVisibility();
        }, 100);
      }
    }, 2000);
  }

  /**
   * Stops the inspection polling
   */
  private stopInspectionPolling(): void {
    if (this.inspectionPollingInterval) {
      clearInterval(this.inspectionPollingInterval);
      this.inspectionPollingInterval = null;
    }
  }

  /**
   * Gets the current total count of inspections across all campaigns
   */
  private getCurrentInspectionCount(): number {
    let totalCount = 0;
    for (const campaign of this.assetManager.asset.InspectionCampaigns) {
      totalCount += campaign.Elements.length;
    }
    return totalCount;
  }

}
